import React, { useState } from 'react';
import {
    IonContent, IonCard, IonCardContent, IonCardTitle,
    IonGrid, IonRow, IonCol, IonIcon, IonText, IonSpinner,
} from '@ionic/react';
import {
    constructOutline, imageOutline, printOutline, documentTextOutline,
    calendarOutline, peopleOutline, trendingUpOutline, barChartOutline, arrowForwardOutline,
    locationOutline, saveOutline, colorPaletteOutline,
    schoolOutline, businessOutline, listOutline, cloudUploadOutline,
} from 'ionicons/icons';
import { useIonViewWillEnter } from '@ionic/react';
import PageLayout from '@/components/navigation/PageLayout';
import { usePageTitle } from '@/contexts/PageTitleContext';
import { apiService } from '@/services/ApiService';
import permissionService from '@/services/PermissionService';
import { ContractDetailsResponse } from '@/types/response/InspectionResultResponses';
import { SitesResponse, ExpertiseAreasResponse, ClassificationsResponse, FrequenciesResponse } from '@/types/response/lookupResponses';
import { FrequencyDetailResponse, FrequencyProfileResponse } from '@/types/response/lookupResponses';

import ModuleImageModal from '@/components/admin/module-settings/ModuleImageModal';
import ImportModal from '@/components/admin/module-settings/ImportModal';
import SettingsModal from '@/components/common/SettingsModal';
import LookupCrudModal from '@/components/common/LookupCrudModal';
import * as msa from '@/services/util/adapters/moduleSettingsAdapters';

interface ModalFlags {
    moduleDetails: boolean;
    moduleImage: boolean;
    reportSettings: boolean;
    transactionLogging: boolean;
    inspectionScheduling: boolean;
    userTypeImpact: boolean;
    frequencyParameters: boolean;
    frequencyProfiles: boolean;
    fixedDateForwarding: boolean;
    defaultSite: boolean;
    saveInProgress: boolean;
    resultsHighlighting: boolean;
    areas: boolean;
    sites: boolean;
    classifications: boolean;
    importGroups: boolean;
    importPlans: boolean;
}

const CLOSED_FLAGS: ModalFlags = {
    moduleDetails: false, moduleImage: false, reportSettings: false,
    transactionLogging: false, inspectionScheduling: false, userTypeImpact: false,
    frequencyParameters: false, frequencyProfiles: false, fixedDateForwarding: false,
    defaultSite: false, saveInProgress: false, resultsHighlighting: false,
    areas: false, sites: false, classifications: false,
    importGroups: false, importPlans: false,
};

const ModuleSettings: React.FC = () => {
    usePageTitle('Module Settings');
    const [isLoading, setIsLoading] = useState(false);
    const [contractDetails, setContractDetails] = useState<ContractDetailsResponse | null>(null);
    const [sites, setSites] = useState<SitesResponse[]>([]);
    const [areas, setAreas] = useState<ExpertiseAreasResponse[]>([]);
    const [classifications, setClassifications] = useState<ClassificationsResponse[]>([]);
    const [frequencies, setFrequencies] = useState<FrequenciesResponse[]>([]);
    const [frequencyDetails, setFrequencyDetails] = useState<FrequencyDetailResponse[]>([]);
    const [frequencyProfiles, setFrequencyProfiles] = useState<FrequencyProfileResponse[]>([]);
    const [open, setOpen] = useState<ModalFlags>({ ...CLOSED_FLAGS });

    const isSuperAdmin = permissionService.isSuperAdmin();
    const canEditAttachments = permissionService.hasRole('EditAttachments');

    const loadData = async () => {
        setIsLoading(true);
        try {
            const details = await apiService.getContractDetails();
            setContractDetails(details);
            setSites((details.Sites ?? []).map(site => ({ Id: site.Id, Name: site.Name, IsActive: site.Active })));
            setAreas(details.ExpertiseAreas ?? []);
            setClassifications((details.Classifications ?? []).map(classification => ({
                Id: classification.Id,
                Name: classification.Name,
                IsActive: classification.Active,
            })));
            setFrequencies(details.Frequencies ?? []);
            setFrequencyDetails(details.Frequencies ?? []);
            setFrequencyProfiles(details.Profiles ?? []);
        } catch (err) {
            console.error('Failed to load module settings data:', err);
        } finally {
            setIsLoading(false);
        }
    };

    useIonViewWillEnter(() => {
        loadData();
    });

    const openModal = (key: keyof ModalFlags) => setOpen(prev => ({ ...prev, [key]: true }));
    const closeModal = (key: keyof ModalFlags) => setOpen(prev => ({ ...prev, [key]: false }));

    interface TaskCard {
        key: keyof ModalFlags;
        icon: string;
        title: string;
        description: string;
        visible?: boolean;
    }

    const sections: Array<{ heading: string; cards: TaskCard[] }> = [
        {
            heading: 'General',
            cards: [
                { key: 'moduleDetails', icon: constructOutline, title: 'Module Details', description: 'Configure module name and customer feedback settings' },
                { key: 'moduleImage', icon: imageOutline, title: 'Module Image', description: 'Upload and manage module images and logo' },
                { key: 'reportSettings', icon: printOutline, title: 'Report Settings', description: 'Configure report header display and footer text' },
                { key: 'transactionLogging', icon: documentTextOutline, title: 'Transaction Logging', description: 'Control which changes are logged in the audit trail' },
            ],
        },
        {
            heading: 'Inspection',
            cards: [
                { key: 'inspectionScheduling', icon: calendarOutline, title: 'Inspection Scheduling', description: 'Configure how N/A and disapproved inspections affect scheduling' },
                { key: 'userTypeImpact', icon: peopleOutline, title: 'User Type Impact', description: 'Set which user types trigger frequency adjustments' },
                { key: 'frequencyParameters', icon: trendingUpOutline, title: 'Frequency Parameters', description: 'Define frequency levels (name, days, history window)' },
                { key: 'frequencyProfiles', icon: barChartOutline, title: 'Frequency Profiles', description: 'Configure auto-adjustment profiles with thresholds' },
                { key: 'fixedDateForwarding', icon: arrowForwardOutline, title: 'Fixed Date Forwarding', description: 'Set past-due threshold for automatic date forwarding' },
            ],
        },
        {
            heading: 'Defaults',
            cards: [
                { key: 'defaultSite', icon: locationOutline, title: 'Default Site', description: 'Set the default site for new inspections' },
                { key: 'saveInProgress', icon: saveOutline, title: 'Save In Progress', description: 'Configure default score and auto-save interval for inspections' },
                { key: 'resultsHighlighting', icon: colorPaletteOutline, title: 'Results Highlighting', description: 'Set score thresholds for good and poor result highlighting' },
            ],
        },
        {
            heading: 'Lookup Data',
            cards: [
                { key: 'areas', icon: schoolOutline, title: 'Areas of Expertise', description: 'Manage areas of expertise used in inspection plans' },
                { key: 'sites', icon: businessOutline, title: 'Sites', description: 'Manage active and inactive site locations' },
                { key: 'classifications', icon: listOutline, title: 'Classifications', description: 'Manage corrective action classification codes' },
            ],
        },
        {
            heading: 'Import',
            cards: [
                { key: 'importGroups', icon: cloudUploadOutline, title: 'Import Groups & Line Items', description: 'Bulk import group and line item structure from Excel', visible: isSuperAdmin },
                { key: 'importPlans', icon: cloudUploadOutline, title: 'Import Inspection Plans', description: 'Bulk import inspection plan details from Excel', visible: isSuperAdmin },
            ],
        },
    ];

    return (
        <PageLayout>
            <IonContent>
                {isLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
                        <IonSpinner />
                    </div>
                ) : (
                    <div style={{ padding: '16px' }}>
                        {sections.map(section => {
                            const visibleCards = section.cards.filter(c => c.visible !== false);
                            if (visibleCards.length === 0) return null;
                            return (
                                <div key={section.heading} style={{ marginBottom: '1.5rem' }}>
                                    <IonText color="medium">
                                        <h6 style={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.75rem', fontWeight: '700', marginBottom: '0.5rem', paddingLeft: '4px' }}>
                                            {section.heading}
                                        </h6>
                                    </IonText>
                                    <IonGrid style={{ padding: 0 }}>
                                        <IonRow>
                                            {visibleCards.map(card => (
                                                <IonCol key={card.key} size="12" sizeMd="6" sizeLg="4">
                                                    <IonCard button onClick={() => openModal(card.key)} style={{ cursor: 'pointer', margin: '4px' }}>
                                                        <IonCardContent style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '16px' }}>
                                                            <IonIcon icon={card.icon} style={{ fontSize: '2rem', color: 'var(--ion-color-primary)', flexShrink: 0 }} />
                                                            <div>
                                                                <IonCardTitle style={{ fontSize: '1rem', marginBottom: '4px' }}>{card.title}</IonCardTitle>
                                                                <IonText color="medium" style={{ fontSize: '0.875rem' }}>{card.description}</IonText>
                                                            </div>
                                                        </IonCardContent>
                                                    </IonCard>
                                                </IonCol>
                                            ))}
                                        </IonRow>
                                    </IonGrid>
                                </div>
                            );
                        })}
                    </div>
                )}

                <SettingsModal<msa.ModuleDetailsForm> isOpen={open.moduleDetails} onDidDismiss={() => closeModal('moduleDetails')}
                    title="Module Details" fields={msa.moduleDetailsFields} emptyData={msa.moduleDetailsEmptyForm}
                    initialData={msa.moduleDetailsInitialData(contractDetails)} onSave={msa.saveModuleDetails}
                    requireReason reasonTitle="Save Module Details" onSaved={() => loadData()} />

                <ModuleImageModal isOpen={open.moduleImage} onDidDismiss={() => closeModal('moduleImage')}
                    currentLogo={contractDetails?.Logo as string | null ?? null}
                    canEditAttachments={canEditAttachments} onSaved={() => loadData()} />

                <SettingsModal<msa.ReportSettingsForm> isOpen={open.reportSettings} onDidDismiss={() => closeModal('reportSettings')}
                    title="Header and Footer for Reports" fields={msa.reportSettingsFields} emptyData={msa.reportSettingsEmptyForm}
                    initialData={msa.reportSettingsInitialData(contractDetails)} onSave={msa.saveReportSettings}
                    requireReason reasonTitle="Save Report Settings" onSaved={() => loadData()} />

                <SettingsModal<msa.TransactionLoggingForm> isOpen={open.transactionLogging} onDidDismiss={() => closeModal('transactionLogging')}
                    title="Transaction Logging" fields={msa.transactionLoggingFields} emptyData={msa.transactionLoggingEmptyForm}
                    initialData={msa.transactionLoggingInitialData(contractDetails)} onSave={msa.saveTransactionLogging}
                    requireReason reasonTitle="Save Transaction Logging" onSaved={() => loadData()} />

                <SettingsModal<msa.InspectionSchedulingForm> isOpen={open.inspectionScheduling} onDidDismiss={() => closeModal('inspectionScheduling')}
                    title="Inspection Scheduling Details" fields={msa.inspectionSchedulingFields} emptyData={msa.inspectionSchedulingEmptyForm}
                    initialData={msa.inspectionSchedulingInitialData(contractDetails)} onSave={msa.saveInspectionScheduling}
                    requireReason reasonTitle="Save Inspection Scheduling" onSaved={() => loadData()} />

                <SettingsModal<msa.UserTypeImpactForm> isOpen={open.userTypeImpact} onDidDismiss={() => closeModal('userTypeImpact')}
                    title="User Type Impact on Schedule and Frequency" fields={msa.userTypeImpactFields} emptyData={msa.userTypeImpactEmptyForm}
                    initialData={msa.userTypeImpactInitialData(contractDetails)} onSave={msa.saveUserTypeImpact}
                    requireReason reasonTitle="Save User Type Impact" onSaved={() => loadData()} />

                <LookupCrudModal<FrequencyDetailResponse, msa.FrequencyParameterRow> isOpen={open.frequencyParameters} onDidDismiss={() => closeModal('frequencyParameters')}
                    title="Frequency Auto-Adjustment Parameters" items={frequencyDetails} columns={msa.frequencyParameterColumns}
                    rowFields={msa.frequencyParameterRowFields} emptyRow={msa.frequencyParameterEmptyRow}
                    getEditingId={f => f.Id} toRow={msa.frequencyParameterToRow} onSaveRow={msa.saveFrequencyParameterRow}
                    saveReasonTitle={row => (row.id != null ? 'Edit Frequency' : 'Add Frequency')}
                    onSaved={() => { void loadData(); }} />

                <LookupCrudModal<FrequencyProfileResponse, msa.FrequencyProfileRow> isOpen={open.frequencyProfiles} onDidDismiss={() => closeModal('frequencyProfiles')}
                    title="Frequency Auto-Adjustment Profiles" items={frequencyProfiles} columns={msa.frequencyProfileColumns(frequencies)}
                    rowFields={msa.frequencyProfileRowFields(frequencies)} emptyRow={msa.frequencyProfileEmptyRow}
                    getEditingId={p => p.Id} toRow={msa.frequencyProfileToRow} onSaveRow={msa.saveFrequencyProfileRow}
                    saveReasonTitle={row => (row.id != null ? 'Edit Profile' : 'Add Profile')}
                    onSaved={() => { void loadData(); }} />

                <SettingsModal<msa.FixedDateForwardingForm> isOpen={open.fixedDateForwarding} onDidDismiss={() => closeModal('fixedDateForwarding')}
                    title="Fixed Inspection Date Forwarding" fields={msa.fixedDateForwardingFields} emptyData={msa.fixedDateForwardingEmptyForm}
                    initialData={msa.fixedDateForwardingInitialData(contractDetails)} onSave={msa.saveFixedDateForwarding}
                    requireReason reasonTitle="Save Fixed Date Forwarding" onSaved={() => loadData()} />

                <SettingsModal<msa.DefaultSiteForm> isOpen={open.defaultSite} onDidDismiss={() => closeModal('defaultSite')}
                    title="Default Site for New Inspections" fields={msa.defaultSiteFields(sites)} emptyData={msa.defaultSiteEmptyForm}
                    initialData={msa.defaultSiteInitialData(contractDetails)} onSave={msa.saveDefaultSite}
                    requireReason reasonTitle="Save Default Site" onSaved={() => loadData()} />

                <SettingsModal<msa.SaveInProgressForm> isOpen={open.saveInProgress} onDidDismiss={() => closeModal('saveInProgress')}
                    title="Inspection Result Save in Progress" fields={msa.saveInProgressFields} emptyData={msa.saveInProgressEmptyForm}
                    initialData={msa.saveInProgressInitialData(contractDetails)} onSave={msa.saveSaveInProgress}
                    requireReason reasonTitle="Save In-Progress Settings" onSaved={() => loadData()} />

                <SettingsModal<msa.ResultsHighlightingForm> isOpen={open.resultsHighlighting} onDidDismiss={() => closeModal('resultsHighlighting')}
                    title="Inspection Results Highlighting" fields={msa.resultsHighlightingFields} emptyData={msa.resultsHighlightingEmptyForm}
                    initialData={msa.resultsHighlightingInitialData(contractDetails)} onSave={msa.saveResultsHighlighting}
                    validateForm={msa.resultsHighlightingValidateForm}
                    requireReason reasonTitle="Save Results Highlighting" onSaved={() => loadData()} />

                <LookupCrudModal<ExpertiseAreasResponse, msa.AreaRow> isOpen={open.areas} onDidDismiss={() => closeModal('areas')}
                    title="Areas of Expertise" items={areas} columns={msa.areaColumns} rowFields={msa.areaRowFields}
                    emptyRow={msa.areaEmptyRow} getEditingId={a => a.Id} toRow={msa.areaToRow} onSaveRow={msa.saveAreaRow}
                    saveReasonTitle={() => 'Save Area of Expertise'}
                    onSaved={() => { void loadData(); }} />

                <LookupCrudModal<SitesResponse, msa.SiteRow> isOpen={open.sites} onDidDismiss={() => closeModal('sites')}
                    title="Sites" items={sites} columns={msa.siteColumns} rowFields={msa.siteRowFields(sites)}
                    emptyRow={msa.siteEmptyRow} getEditingId={s => s.Id} toRow={msa.siteToRow} onSaveRow={msa.saveSiteRow}
                    saveReasonTitle={row => (row.id != null ? 'Edit Site' : 'Add Site')} activateAction={msa.activateSiteAction}
                    onSaved={() => { void loadData(); }} />

                <LookupCrudModal<ClassificationsResponse, msa.ClassificationRow> isOpen={open.classifications} onDidDismiss={() => closeModal('classifications')}
                    title="Corrective Action Classifications" items={classifications} columns={msa.classificationColumns}
                    rowFields={msa.classificationRowFields(classifications)} emptyRow={msa.classificationEmptyRow}
                    getEditingId={c => c.Id} toRow={msa.classificationToRow} onSaveRow={msa.saveClassificationRow}
                    saveReasonTitle={row => (row.id != null ? 'Edit Classification' : 'Add Classification')}
                    activateAction={msa.activateClassificationAction}
                    onSaved={() => { void loadData(); }} />

                <ImportModal isOpen={open.importGroups} onDidDismiss={() => closeModal('importGroups')} importType={1} />
                <ImportModal isOpen={open.importPlans} onDidDismiss={() => closeModal('importPlans')} importType={2} />
            </IonContent>
        </PageLayout>
    );
};

export default ModuleSettings;
